import React from 'react';
import { InvoiceData, InvoiceSettings } from '../../../types';

interface Props {
  data: InvoiceData;
  settings: InvoiceSettings;
  labels: Record<string, string>;
  subtotal: number;
  taxTotal: number;
  total: number;
}

const ModernTemplate: React.FC<Props> = ({ data, settings, subtotal, taxTotal, total }) => {
  const currencySymbol = settings.currencySymbol || '$';

  // Dynamic status styling
  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'Paid':
        return {
          color: 'text-emerald-500',
          bg: 'bg-emerald-50',
          border: 'border-emerald-100',
          label: 'Settled',
        };
      case 'Sent':
        return {
          color: 'text-blue-500',
          bg: 'bg-blue-50',
          border: 'border-blue-100',
          label: 'Outstanding',
        };
      default:
        return {
          color: 'text-slate-400',
          bg: 'bg-slate-50',
          border: 'border-slate-100',
          label: 'Draft',
        };
    }
  };

  const status = getStatusConfig(data.status || 'Draft');

  return (
    <div className="p-10 md:p-16 max-w-4xl mx-auto bg-white text-slate-900 min-h-screen flex flex-col font-sans relative">
      {/* Structural Accents */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-slate-50 rounded-bl-[5rem] -z-0"></div>

      {/* Header Pipeline */}
      <div className="relative z-10 flex flex-col md:flex-row justify-between items-start mb-20 gap-12">
        <div className="space-y-8">
          <div className="flex items-center gap-4">
            {data.logo ? (
              <div className="bg-white p-2 rounded-2xl shadow-sm border border-slate-100">
                <img src={data.logo} alt="Logo" className="h-14 w-auto object-contain" />
              </div>
            ) : (
              <div className="w-14 h-14 bg-slate-900 rounded-2xl flex items-center justify-center text-white font-black text-xl">
                {data.senderName ? data.senderName.charAt(0) : 'S'}
              </div>
            )}
            <div>
              <h1 className="text-xl font-black tracking-tighter text-slate-900 uppercase leading-none">
                {data.senderName || 'Sender Entity'}
              </h1>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">
                Origin Node
              </p>
            </div>
          </div>

          <div className="space-y-1">
            <h4 className="text-[9px] text-slate-300 font-black uppercase tracking-[0.3em]">
              Address & Contact
            </h4>
            <p className="text-xs font-bold leading-relaxed text-slate-500 whitespace-pre-wrap max-w-xs">
              {data.senderAddress}
              {data.senderPhone && `\n${data.senderPhone}`}
            </p>
          </div>
        </div>

        <div className="md:text-right flex flex-col md:items-end">
          <div className="mb-8">
            <div
              className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full border text-[10px] font-black uppercase tracking-widest ${status.bg} ${status.color} ${status.border}`}
            >
              <div
                className={`w-1.5 h-1.5 rounded-full ${status.color.replace('text', 'bg')} animate-pulse`}
              ></div>
              {status.label}
            </div>
          </div>

          <div className="space-y-4">
            <div className="space-y-0.5">
              <span className="block text-[9px] font-black text-slate-300 uppercase tracking-[0.2em]">
                Index Number
              </span>
              <span className="text-lg font-black text-slate-900 font-mono">#{data.number}</span>
            </div>
            <div className="grid grid-cols-2 gap-8 md:block md:space-y-4">
              <div className="space-y-0.5">
                <span className="block text-[9px] font-black text-slate-300 uppercase tracking-[0.2em]">
                  Release Date
                </span>
                <span className="text-xs font-bold text-slate-600 font-mono">{data.date}</span>
              </div>
              <div className="space-y-0.5">
                <span className="block text-[9px] font-black text-slate-300 uppercase tracking-[0.2em]">
                  Maturity
                </span>
                <span
                  className={`text-xs font-black font-mono ${data.dueDate ? 'text-red-500' : 'text-slate-400'}`}
                >
                  {data.dueDate || 'N/A'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recipient & Settlement Summary */}
      <div className="relative z-10 grid grid-cols-1 md:grid-cols-12 gap-12 mb-20">
        <div className="md:col-span-7 bg-slate-50 p-8 rounded-[2rem] border border-slate-100">
          <h4 className="text-[9px] text-brand-500 font-black uppercase tracking-[0.3em] mb-6">
            Target Identity
          </h4>
          <p className="text-2xl font-black mb-3 tracking-tighter text-slate-900 leading-tight">
            {data.recipientCompany || data.recipientName || 'Unspecified Client'}
          </p>
          <p className="text-xs text-slate-500 whitespace-pre-wrap leading-relaxed font-bold">
            {data.recipientAddress}
          </p>
        </div>

        <div className="md:col-span-5 flex flex-col justify-center md:items-end">
          <h4 className="text-[9px] text-slate-300 font-black uppercase tracking-[0.3em] mb-4">
            Final Settlement
          </h4>
          <div className="flex items-start text-slate-900">
            <span className="text-xl font-black mr-1 mt-1">{currencySymbol}</span>
            <span className="text-6xl font-black tracking-tighter leading-none">
              {total.toLocaleString(undefined, { minimumFractionDigits: 0 })}
            </span>
          </div>
        </div>
      </div>

      {/* Line Items Table */}
      <div className="relative z-10 flex-1 overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b-2 border-slate-900">
              <th className="text-left py-6 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">
                Description of Deliverables
              </th>
              <th className="text-center py-6 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 w-20">
                Qty
              </th>
              <th className="text-right py-6 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 w-32">
                Base Rate
              </th>
              <th className="text-right py-6 text-[10px] font-black uppercase tracking-[0.2em] text-slate-900 w-32">
                Amount
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.items.map((item, idx) => (
              <tr key={idx} className="group">
                <td className="py-8 pr-4">
                  <p className="font-bold text-base text-slate-900 leading-snug">
                    {item.description || 'Professional Service'}
                  </p>
                  <p className="text-[9px] font-black text-slate-300 uppercase tracking-widest mt-1">
                    Code: {item.id.slice(0, 4)}
                  </p>
                </td>
                <td className="py-8 text-center text-sm font-black text-slate-400 font-mono">
                  {item.quantity}
                </td>
                <td className="py-8 text-right text-sm font-bold text-slate-500 font-mono">
                  {currencySymbol}
                  {item.rate.toLocaleString()}
                </td>
                <td className="py-8 text-right">
                  <span className="text-lg font-black text-slate-900 tracking-tighter">
                    {currencySymbol}
                    {(item.quantity * item.rate).toLocaleString()}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer Metadata */}
      <div className="relative z-10 mt-20 pt-10 border-t border-slate-100 flex flex-col md:flex-row justify-between gap-12 items-end">
        <div className="max-w-md w-full space-y-8">
          <div className="bg-slate-50 p-6 rounded-3xl border border-slate-100">
            <h4 className="text-[9px] font-black uppercase tracking-[0.3em] text-slate-300 mb-3">
              Remittance & Terms
            </h4>
            <p className="text-[10px] font-bold text-slate-500 leading-relaxed whitespace-pre-wrap italic">
              {data.paymentInfo || 'Net 30. Wire transfer only.'}
            </p>
          </div>

          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full border border-slate-100 flex items-center justify-center text-slate-200">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
            </div>
            <div>
              <p className="text-[9px] font-black uppercase tracking-widest text-slate-300">
                Identity Authentication
              </p>
              <p className="text-[10px] font-bold text-slate-200 font-mono">
                DOC_VERIFIED_HASH: {data.id.slice(0, 12)}
              </p>
            </div>
          </div>
        </div>

        <div className="w-full md:w-64 space-y-4">
          <div className="space-y-3 px-2">
            <div className="flex justify-between items-center text-[10px] font-bold text-slate-400 uppercase tracking-widest">
              <span>Subtotal</span>
              <span className="text-slate-900 font-mono">
                {currencySymbol}
                {subtotal.toLocaleString()}
              </span>
            </div>
            {taxTotal > 0 && (
              <div className="flex justify-between items-center text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                <span>Tax {settings.taxLabel}</span>
                <span className="text-slate-900 font-mono">
                  {currencySymbol}
                  {taxTotal.toLocaleString()}
                </span>
              </div>
            )}
          </div>

          <div className="p-8 bg-slate-900 rounded-[2.5rem] shadow-xl text-white text-right">
            <span className="text-[9px] font-black uppercase tracking-[0.4em] text-slate-500 block mb-2">
              Total Payable
            </span>
            <div className="flex items-start justify-end">
              <span className="text-lg font-black text-brand-400 mr-1 mt-1">{currencySymbol}</span>
              <span className="text-4xl font-black tracking-tighter">{total.toLocaleString()}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModernTemplate;
